/**
 * HealthKit Integration Service
 * 
 * This service handles Apple HealthKit integration for iOS devices.
 * It provides methods to check availability, request permissions, and sync health data.
 * 
 * Note: This requires the Natively.Health SDK to be properly configured.
 * Reference: https://natively.dev/docs/health
 * 
 * IMPORTANT: This service only works in native iOS apps, not in web browsers.
 */

import { supabase } from "@/integrations/supabase/client";
import { detectNativeApp } from "./platform";
import { format } from "date-fns";

export interface HealthKitSettings {
  enabled: boolean;
  sync_exercise_time: boolean;
  sync_stand_hours: boolean;
  sync_sleep: boolean;
  sync_steps: boolean;
  sync_bmi: boolean;
  sync_body_mass: boolean;
  sync_heart_rate: boolean;
  sync_active_energy: boolean;
}

export interface HealthKitData {
  date: string;
  exercise_time?: number;
  stand_hours?: number;
  sleep_time?: number;
  steps?: number;
  bmi?: number;
  body_mass?: number;
  heart_rate?: number;
  active_energy?: number;
}

/**
 * Check if HealthKit is available on the current device
 */
export const checkHealthKitAvailability = async (): Promise<boolean> => {
  // First check if running in native app using async detection
  const detection = await detectNativeApp();
  
  if (!detection.isNative) {
    console.log('HealthKit: Not available (running in web browser, not native app)');
    return false;
  }

  if (typeof window === 'undefined' || !window.NativelyHealth) {
    console.log('HealthKit: Not available (NativelyHealth SDK not loaded)');
    return false;
  }

  console.log('HealthKit: Calling NativelyHealth() to get health instance...');
  
  return new Promise((resolve) => {
    try {
      // Instantiate NativelyHealth with 'new' keyword
      const health = new window.NativelyHealth();
      
      console.log('HealthKit: health instance created, calling available()...');
      
      // Set a timeout in case the callback never fires
      const timeout = setTimeout(() => {
        console.error('HealthKit: available() callback timeout after 5 seconds');
        resolve(false);
      }, 5000);
      
      health.available((res, err) => {
        clearTimeout(timeout);
        
        if (err) {
          console.error('HealthKit availability check error:', err);
          resolve(false);
          return;
        }
        console.log('HealthKit available:', res.status);
        resolve(res.status);
      });
    } catch (error) {
      console.error('HealthKit: Error calling NativelyHealth():', error);
      resolve(false);
    }
  });
};

/**
 * Request HealthKit authorization for specific data types
 */
export const requestHealthKitAuthorization = async (
  readTypes: string[]
): Promise<boolean> => {
  // Check if running in native app using async detection
  const detection = await detectNativeApp();
  
  if (!detection.isNative) {
    throw new Error('HealthKit not available in web browser');
  }

  if (typeof window === 'undefined' || !window.NativelyHealth) {
    throw new Error('HealthKit not available');
  }

  console.log('HealthKit: Requesting authorization for:', readTypes);
  
  return new Promise((resolve, reject) => {
    try {
      const health = new window.NativelyHealth();
      
      // Set a timeout in case the callback never fires
      const timeout = setTimeout(() => {
        console.error('HealthKit: requestAuthorization() callback timeout after 10 seconds');
        reject(new Error('HealthKit authorization request timeout'));
      }, 10000);
      
      health.requestAuthorization([], readTypes, (res, err) => {
        clearTimeout(timeout);
        
        if (err) {
          console.error('HealthKit authorization error:', err);
          reject(new Error(err));
          return;
        }
        
        // Note: res.status can be false if:
        // - User denied some/all permissions
        // - User is seeing the popup for the first time
        // - Permissions are partially granted
        // This is normal behavior - we just log it and resolve successfully
        console.log('HealthKit authorization response:', res.status);
        
        if (!res.status) {
          console.log('HealthKit: User may have denied or partially granted permissions');
        }
        
        // Always resolve successfully - the popup was shown
        resolve(res.status);
      });
    } catch (error) {
      console.error('HealthKit: Error requesting authorization:', error);
      reject(error instanceof Error ? error : new Error(String(error)));
    }
  });
};

/**
 * Get user's HealthKit settings from the database
 */
export const getHealthKitSettings = async (
  userId: string
): Promise<HealthKitSettings | null> => {
  const { data, error } = await supabase
    .from('healthkit_settings')
    .select('*')
    .eq('user_id', userId)
    .maybeSingle();

  if (error) {
    console.error('Error fetching HealthKit settings:', error);
    return null;
  }

  // Ensure all required fields are present with defaults for new fields
  if (data) {
    return {
      ...data,
      sync_bmi: data.sync_bmi ?? false,
      sync_body_mass: data.sync_body_mass ?? false,
      sync_heart_rate: data.sync_heart_rate ?? false,
      sync_active_energy: data.sync_active_energy ?? false,
    } as HealthKitSettings;
  }

  return null;
};

/**
 * Update user's HealthKit settings
 */
export const updateHealthKitSettings = async (
  userId: string,
  settings: Partial<HealthKitSettings>
): Promise<boolean> => {
  try {
    // Only update fields that exist in the current database schema
    // This prevents errors for new fields that may not be in the database yet
    const updateData: any = {
      user_id: userId,
      updated_at: new Date().toISOString(),
    };
    
    // Add only the fields that are being updated
    if ('enabled' in settings) updateData.enabled = settings.enabled;
    if ('sync_exercise_time' in settings) updateData.sync_exercise_time = settings.sync_exercise_time;
    if ('sync_stand_hours' in settings) updateData.sync_stand_hours = settings.sync_stand_hours;
    if ('sync_sleep' in settings) updateData.sync_sleep = settings.sync_sleep;
    if ('sync_steps' in settings) updateData.sync_steps = settings.sync_steps;
    
    // New fields - only include if they're being updated
    if ('sync_bmi' in settings) updateData.sync_bmi = settings.sync_bmi;
    if ('sync_body_mass' in settings) updateData.sync_body_mass = settings.sync_body_mass;
    if ('sync_heart_rate' in settings) updateData.sync_heart_rate = settings.sync_heart_rate;
    if ('sync_active_energy' in settings) updateData.sync_active_energy = settings.sync_active_energy;
    
    const { error } = await supabase
      .from('healthkit_settings')
      .upsert(updateData, {
        onConflict: 'user_id',
      });

    if (error) {
      console.error('Error updating HealthKit settings:', error);
      // If the error is about missing columns, just log it but return true
      // This allows the app to work even if database schema isn't updated yet
      if (error.message?.includes('column') || error.message?.includes('does not exist')) {
        console.warn('HealthKit: Database schema may need updating for new metrics');
        return true; // Return true to allow UI to continue working
      }
      return false;
    }

    return true;
  } catch (error) {
    console.error('Error in updateHealthKitSettings:', error);
    return false;
  }
};

/**
 * Calculate sleep hours from sleep analysis data
 */
const calculateSleepHours = (
  sleepData: Array<any>
): number => {
  console.log('[HealthKit] Calculating sleep from entries:', sleepData.length);
  
  // Handle both formats:
  // 1. { value: 'ASLEEP', startDate: timestamp, endDate: timestamp }
  // 2. { type: 'Asleep unspecified', startDate: timestamp, endDate: timestamp }
  const asleepEntries = sleepData.filter((entry) => {
    let checkValue = '';
    
    // Check for 'value' property (format 1)
    if (entry.value) {
      checkValue = entry.value.toLowerCase();
    }
    // Check for 'type' property (format 2)
    else if (entry.type) {
      checkValue = entry.type.toLowerCase();
    }
    
    // Include any sleep states: asleep, core, deep, rem
    // Exclude awake states
    return (
      (checkValue.includes('asleep') || 
       checkValue.includes('core') || 
       checkValue.includes('deep') || 
       checkValue.includes('rem')) &&
      !checkValue.includes('awake')
    );
  });
  
  console.log('[HealthKit] ASLEEP entries found:', asleepEntries.length);
  
  // Store breakdown for debugging
  const breakdown: string[] = [];
  
  const totalSleepSeconds = asleepEntries.reduce((total, entry) => {
    // Duration is in milliseconds, convert to seconds
    const duration = (entry.endDate - entry.startDate) / 1000;
    const hours = duration / 3600;
    const typeOrValue = entry.type || entry.value || 'unknown';
    console.log(`[HealthKit] Sleep entry: ${typeOrValue}, duration: ${duration}s (${hours.toFixed(2)}hrs)`);
    breakdown.push(`${typeOrValue}: ${hours.toFixed(2)}hrs`);
    return total + duration;
  }, 0);

  const hours = totalSleepSeconds / 3600;
  console.log('[HealthKit] Total sleep hours:', hours);
  
  // Store breakdown in window for toast display
  (window as any).__healthKitSleepBreakdown = breakdown;
  
  return hours;
};

/**
 * Normalize a HealthKit timestamp to a JavaScript Date.
 * Natively SDK may return timestamps in seconds or milliseconds.
 */
const normalizeHealthKitTimestamp = (ts: number): Date => {
  // If less than 1e10 (roughly year 2001 in ms but year 2286 in seconds),
  // treat as seconds. A timestamp in ms for year 2023 would be ~1.67e12.
  if (ts < 1e10) {
    return new Date(ts * 1000);
  }
  return new Date(ts);
};

/**
 * Group sleep analysis entries by date and calculate total sleep hours per date.
 * Sleep is attributed to the wake-up date (endDate).
 */
const groupSleepEntriesByDate = (entries: any[]): Map<string, number> => {
  const sleepByDate = new Map<string, number>();
  
  for (const entry of entries) {
    // Filter to only asleep entries (not awake, not in-bed only)
    let checkValue = '';
    if (entry.value) checkValue = entry.value.toLowerCase();
    else if (entry.type) checkValue = entry.type.toLowerCase();
    
    const isAsleep = (
      checkValue.includes('asleep') || 
      checkValue.includes('core') || 
      checkValue.includes('deep') || 
      checkValue.includes('rem')
    ) && !checkValue.includes('awake');
    
    if (!isAsleep) continue;
    
    // Calculate duration
    const startTs = normalizeHealthKitTimestamp(entry.startDate);
    const endTs = normalizeHealthKitTimestamp(entry.endDate);
    const durationSeconds = (endTs.getTime() - startTs.getTime()) / 1000;
    
    if (durationSeconds <= 0) continue;
    
    // Attribute to the wake-up date (endDate)
    const dateStr = format(endTs, 'yyyy-MM-dd');
    const currentHours = sleepByDate.get(dateStr) || 0;
    sleepByDate.set(dateStr, currentHours + durationSeconds / 3600);
  }
  
  return sleepByDate;
};

/**
 * Log detailed entries for HealthKit synced data so they appear when clicking day squares.
 * Clears previous HealthKit entries in the range to avoid duplicates on re-sync.
 */
const logHealthKitDetailedEntries = async (
  userId: string,
  data: HealthKitData[],
  startDateStr: string,
  endDateStr: string
): Promise<void> => {
  try {
    // 1. Delete existing HealthKit detailed entries in the range
    // These are identified by their metric_key starting with "healthkit_"
    const healthKitMetricKeys = ['healthkit_steps', 'healthkit_sleep_time'];
    
    const { error: deleteError } = await supabase
      .from('detailed_entries')
      .delete()
      .eq('user_id', userId)
      .gte('entry_date', startDateStr)
      .lte('entry_date', endDateStr)
      .in('metric_key', healthKitMetricKeys);
    
    if (deleteError) {
      console.warn('[HealthKit] Error clearing old detailed entries:', deleteError);
      // Continue anyway - duplicates are better than no entries
    }

    // 2. Build new entries
    const entries: any[] = [];
    const now = new Date().toISOString();
    
    for (const dayData of data) {
      if (dayData.steps != null && dayData.steps > 0) {
        entries.push({
          user_id: userId,
          entry_date: dayData.date,
          metric_key: 'healthkit_steps',
          entry_text: `🍎 Apple Health synced ${Number(dayData.steps).toLocaleString()} steps`,
          entry_time: now,
        });
      }
      if (dayData.sleep_time != null && dayData.sleep_time > 0) {
        entries.push({
          user_id: userId,
          entry_date: dayData.date,
          metric_key: 'healthkit_sleep_time',
          entry_text: `🍎 Apple Health synced ${dayData.sleep_time.toFixed(1)} hrs sleep`,
          entry_time: now,
        });
      }
    }

    // 3. Batch insert in chunks (avoid too-large payloads)
    if (entries.length > 0) {
      const chunkSize = 100;
      for (let i = 0; i < entries.length; i += chunkSize) {
        const chunk = entries.slice(i, i + chunkSize);
        const { error } = await supabase.from('detailed_entries').insert(chunk);
        if (error) {
          console.error('[HealthKit] Error inserting detailed entries chunk:', error);
        }
      }
      console.log(`[HealthKit] Logged ${entries.length} detailed entries for ${data.length} days`);
    }
  } catch (error) {
    console.error('[HealthKit] Error logging detailed entries:', error);
    // Non-critical - don't throw
  }
};

/**
 * Sync HealthKit data for a specific date range
 */
export const syncHealthKitData = async (
  userId: string,
  settings: HealthKitSettings,
  startDate: Date,
  endDate: Date
): Promise<HealthKitData[]> => {
  // Track sync results for debugging
  const syncResults: { metric: string; success: boolean; error?: string; value?: any }[] = [];
  
  // Check if running in native app using async detection
  const detection = await detectNativeApp();
  
  if (!detection.isNative) {
    throw new Error('HealthKit not available in web browser');
  }

  if (typeof window === 'undefined' || !window.NativelyHealth) {
    throw new Error('HealthKit not available');
  }

  const health = new window.NativelyHealth();
  
  // Request authorization before syncing
  const readDataTypes: string[] = [];
  if (settings.sync_steps) readDataTypes.push('STEPS');
  if (settings.sync_sleep) readDataTypes.push('SLEEP_ANALYSIS');
  
  if (readDataTypes.length > 0) {
    console.log('[HealthKit] Requesting authorization for:', readDataTypes);
    await new Promise<void>((resolve, reject) => {
      health.requestAuthorization([], readDataTypes, (res: any, err: any) => {
        if (err) {
          console.error('[HealthKit] Authorization error:', err);
          reject(new Error(`Authorization failed: ${err}`));
          return;
        }
        console.log('[HealthKit] Authorization granted:', res);
        resolve();
      });
    });
  }
  
  const syncedData: HealthKitData[] = [];

  // Log which settings are enabled for debugging
  // NOTE: Only Steps and Sleep are currently supported
  console.log('[HealthKit] Sync settings (supported metrics only):', {
    sleep: settings.sync_sleep,
    steps: settings.sync_steps,
  });
  
  // These metrics are coming soon and will be ignored even if enabled:
  if (settings.sync_exercise_time || settings.sync_stand_hours || settings.sync_bmi || 
      settings.sync_body_mass || settings.sync_heart_rate || settings.sync_active_energy) {
    console.log('[HealthKit] Note: Exercise Time, Stand Hours, BMI, Body Mass, Heart Rate, and Active Energy are coming soon and will not be synced.');
  }

  // Create date for the target day
  const targetDate = new Date(startDate);
  targetDate.setHours(0, 0, 0, 0);
  
  const dayEnd = new Date(targetDate);
  dayEnd.setHours(23, 59, 59, 999);

  const dataPoint: HealthKitData = {
    date: targetDate.toISOString().split('T')[0],
  };

  try {
    // Fetch Activity Summary (exercise time, stand hours)
    // NOTE: Exercise Time and Stand Hours are disabled (coming soon features)
    // Commenting out this code block to prevent sync attempts
    // if (settings.sync_exercise_time || settings.sync_stand_hours) {
    //   await new Promise<void>((resolve) => {
    //     console.log('[HealthKit] Fetching ACTIVITY_SUMMARY for date:', targetDate.toISOString());
    //     health.getActivitySummary(targetDate, dayEnd, (res, err) => {
    //       if (err) {
    //         console.error('[HealthKit] Error fetching activity summary:', err);
    //         syncResults.push({ metric: 'Activity Summary', success: false, error: err });
    //         // Don't fail the entire sync, just skip this metric
    //         resolve();
    //         return;
    //       }

    //       console.log('[HealthKit] Activity Summary response:', res);
    //       if (res.result && res.result.length > 0) {
    //         const activity = res.result[0];
    //         console.log('[HealthKit] Activity data:', activity);
    //         if (settings.sync_exercise_time) {
    //           dataPoint.exercise_time = activity.exerciseTime;
    //           console.log('[HealthKit] Exercise time:', dataPoint.exercise_time);
    //           syncResults.push({ metric: 'Exercise Time', success: true, value: dataPoint.exercise_time });
    //         }
    //         if (settings.sync_stand_hours) {
    //           dataPoint.stand_hours = activity.standHours;
    //           console.log('[HealthKit] Stand hours:', dataPoint.stand_hours);
    //           syncResults.push({ metric: 'Stand Hours', success: true, value: dataPoint.stand_hours });
    //         }
    //       } else {
    //         console.log('[HealthKit] No activity summary data available');
    //         if (settings.sync_exercise_time) syncResults.push({ metric: 'Exercise Time', success: false, error: 'No data available' });
    //         if (settings.sync_stand_hours) syncResults.push({ metric: 'Stand Hours', success: false, error: 'No data available' });
    //       }
    //       resolve();
    //     });
    //   });
    // }

    // Fetch Steps
    if (settings.sync_steps) {
      await new Promise<void>((resolve) => {
        console.log('[HealthKit] Fetching STEPS for date:', targetDate.toISOString());
        health.getStatisticQuantity('STEPS', 'DAY', targetDate, dayEnd, (res, err) => {
          if (err) {
            console.error('[HealthKit] Error fetching steps:', err);
            console.error('[HealthKit] Steps error details:', JSON.stringify(err));
            syncResults.push({ metric: 'Steps', success: false, error: typeof err === 'string' ? err : JSON.stringify(err) });
            // Don't fail the entire sync, just skip this metric
            resolve();
            return;
          }
          console.log('[HealthKit] Steps response:', JSON.stringify(res));
          
          // Extract value from response - result is typically [{ value: number }]
          let stepsValue: number | null = null;
          
          if (res.result !== undefined && res.result !== null) {
            // Check if result is an array and find the object with "value" property
            if (Array.isArray(res.result)) {
              const itemWithValue = res.result.find((item: any) => item.value !== undefined);
              if (itemWithValue) {
                stepsValue = itemWithValue.value;
                console.log('[HealthKit] Steps value extracted from array:', stepsValue);
              }
            }
            // Check if result is an object with value property
            else if (typeof res.result === 'object' && (res.result as any).value !== undefined) {
              stepsValue = (res.result as any).value;
              console.log('[HealthKit] Steps value extracted from object:', stepsValue);
            } 
            // Direct number value
            else if (typeof res.result === 'number') {
              stepsValue = res.result;
              console.log('[HealthKit] Steps result is number:', stepsValue);
            }
          }
          
          if (stepsValue !== null && !isNaN(stepsValue)) {
            const finalValue = Math.round(Number(stepsValue));
            dataPoint.steps = finalValue;
            console.log('[HealthKit] Steps value saved:', dataPoint.steps);
            syncResults.push({ metric: 'Steps', success: true, value: finalValue });
          } else {
            const errorMsg = res.result === undefined || res.result === null 
              ? 'No data available' 
              : `Could not extract value from: ${JSON.stringify(res.result)}`;
            console.log('[HealthKit] Steps data issue:', errorMsg);
            syncResults.push({ metric: 'Steps', success: false, error: errorMsg });
          }
          resolve();
        });
      });
    }

    // Fetch Sleep Analysis
    if (settings.sync_sleep) {
      await new Promise<void>((resolve) => {
        console.log('[HealthKit] Fetching SLEEP_ANALYSIS for date:', targetDate.toISOString());
        health.getDailySleepAnalysis(targetDate, dayEnd, 100, (res, err) => {
          if (err) {
            console.error('[HealthKit] Error fetching sleep analysis:', err);
            syncResults.push({ metric: 'Sleep Time', success: false, error: typeof err === 'string' ? err : JSON.stringify(err) });
            // Don't fail the entire sync, just skip this metric
            resolve();
            return;
          }
          console.log('[HealthKit] Sleep response:', JSON.stringify(res));
          console.log('[HealthKit] Sleep result array length:', res.result?.length);
          
          if (res.result && Array.isArray(res.result) && res.result.length > 0) {
            console.log('[HealthKit] Sleep entries:', JSON.stringify(res.result));
            const sleepHours = calculateSleepHours(res.result);
            dataPoint.sleep_time = sleepHours;
            console.log('[HealthKit] Calculated sleep time:', sleepHours);
            
            if (sleepHours > 0) {
              syncResults.push({ metric: 'Sleep Time', success: true, value: sleepHours });
            } else {
              // Add debug info to show what was returned
              const debugInfo = {
                resultLength: res.result?.length || 0,
                sampleEntries: res.result?.slice(0, 5) || [], // First 5 entries for debugging
                allValues: res.result?.map((e: any) => e.value) || [],
              };
              
              syncResults.push({ 
                metric: 'Sleep Time', 
                success: false, 
                error: `No ASLEEP entries found. Debug: ${JSON.stringify(debugInfo)}` 
              });
            }
          } else {
            console.log('[HealthKit] No sleep data available (empty or null result)');
            syncResults.push({ 
              metric: 'Sleep Time', 
              success: false, 
              error: `No data available. Raw response: ${JSON.stringify(res)}` 
            });
          }
          resolve();
        });
      });
    }

    // NOTE: The following metrics are disabled (coming soon)
    // They are not synced even if settings have them enabled
    
    // Fetch BMI - DISABLED (coming soon)
    // if (settings.sync_bmi) {
    //   await new Promise<void>((resolve) => {
    //     health.getStatisticQuantity('BMI', 'day', targetDate, dayEnd, (res, err) => {
    //       if (err) {
    //         console.error('Error fetching BMI:', err);
    //         resolve();
    //         return;
    //       }
    //       if (res.result) {
    //         dataPoint.bmi = res.result;
    //       }
    //       resolve();
    //     });
    //   });
    // }

    // Fetch Body Mass - DISABLED (coming soon)
    // if (settings.sync_body_mass) {
    //   await new Promise<void>((resolve) => {
    //     health.getStatisticQuantity('BODY_MASS', 'day', targetDate, dayEnd, (res, err) => {
    //       if (err) {
    //         console.error('Error fetching body mass:', err);
    //         resolve();
    //         return;
    //       }
    //       if (res.result) {
    //         dataPoint.body_mass = res.result;
    //       }
    //       resolve();
    //     });
    //   });
    // }

    // Fetch Heart Rate - DISABLED (coming soon)
    // if (settings.sync_heart_rate) {
    //   await new Promise<void>((resolve) => {
    //     health.getStatisticQuantity('HEART_RATE', 'day', targetDate, dayEnd, (res, err) => {
    //       if (err) {
    //         console.error('Error fetching heart rate:', err);
    //         resolve();
    //         return;
    //       }
    //       if (res.result) {
    //         dataPoint.heart_rate = Math.round(res.result);
    //       }
    //       resolve();
    //     });
    //   });
    // }

    // Fetch Active Energy - DISABLED (coming soon)
    // if (settings.sync_active_energy) {
    //   await new Promise<void>((resolve) => {
    //     health.getStatisticQuantity('ACTIVE_ENERGY', 'day', targetDate, dayEnd, (res, err) => {
    //       if (err) {
    //         console.error('Error fetching active energy:', err);
    //         resolve();
    //         return;
    //       }
    //       if (res.result) {
    //         dataPoint.active_energy = res.result;
    //       }
    //       resolve();
    //     });
    //   });
    // }

    syncedData.push(dataPoint);

    console.log('[HealthKit] Synced data point before save:', JSON.stringify(dataPoint, null, 2));
    console.log('[HealthKit] ===== SYNC RESULTS SUMMARY =====');
    syncResults.forEach(result => {
      if (result.success) {
        console.log(`[HealthKit] ✅ ${result.metric}: ${result.value !== undefined ? result.value : 'synced'}`);
      } else {
        console.log(`[HealthKit] ❌ ${result.metric}: ${result.error}`);
      }
    });
    console.log('[HealthKit] ===============================');
    
    // Store results in window for UI access
    if (typeof window !== 'undefined') {
      (window as any).__healthKitLastSyncResults = syncResults;
    }

    // Only save if we have at least the date field (minimum required)
    // Even if all metrics failed, we should update the last sync time
    console.log('[HealthKit] Saving data to database...');
    await saveHealthKitData(userId, [dataPoint]);

    // Update last sync time
    await supabase
      .from('healthkit_settings')
      .update({
        last_sync_at: new Date().toISOString(),
      })
      .eq('user_id', userId);

    console.log('[HealthKit] Data synced and saved successfully:', dataPoint);
  } catch (error) {
    console.error('[HealthKit] Error syncing HealthKit data:', error);
    throw error;
  }

  return syncedData;
};

/**
 * Save HealthKit data to database
 * Saves to both healthkit_data table (historical) and daily_entries table (for dashboard display)
 */
const saveHealthKitData = async (
  userId: string,
  data: HealthKitData[]
): Promise<void> => {
  try {
    console.log(`[HealthKit] saveHealthKitData called with ${data.length} day(s) of data`);
    
    // Chunk size for batch upserts (avoid payload limits)
    const CHUNK_SIZE = 50;
    
    // 1. Save to healthkit_data table (historical data)
    const healthkitRecords = data.map((item) => {
      const record: any = {
        user_id: userId,
        date: item.date,
        synced_at: new Date().toISOString(),
      };
      
      // Only include fields that have values
      if (item.exercise_time !== undefined) record.exercise_time = item.exercise_time;
      if (item.stand_hours !== undefined) record.stand_hours = item.stand_hours;
      if (item.sleep_time !== undefined) record.sleep_time = item.sleep_time;
      if (item.steps !== undefined) record.steps = item.steps;
      
      // New fields - only include if they exist
      if (item.bmi !== undefined) record.bmi = item.bmi;
      if (item.body_mass !== undefined) record.body_mass = item.body_mass;
      if (item.heart_rate !== undefined) record.heart_rate = item.heart_rate;
      if (item.active_energy !== undefined) record.active_energy = item.active_energy;
      
      return record;
    });

    // Upsert in chunks
    for (let i = 0; i < healthkitRecords.length; i += CHUNK_SIZE) {
      const chunk = healthkitRecords.slice(i, i + CHUNK_SIZE);
      const { error: healthkitError } = await supabase.from('healthkit_data').upsert(chunk, {
        onConflict: 'user_id,date',
      });

      if (healthkitError) {
        console.error('[HealthKit] Error saving to healthkit_data table:', healthkitError);
        if (healthkitError.message?.includes('column') || healthkitError.message?.includes('does not exist')) {
          console.warn('[HealthKit] Some fields could not be saved to healthkit_data. Database schema may need updating.');
        } else {
          throw healthkitError;
        }
      }
    }
    console.log(`[HealthKit] Successfully saved ${healthkitRecords.length} record(s) to healthkit_data`);

    // 2. Also save to daily_entries table (for dashboard display)
    const entryRecords = data.map((item) => {
      const record: any = {
        user_id: userId,
        entry_date: item.date,
      };
      
      // Map to healthkit_* columns in daily_entries
      if (item.exercise_time !== undefined) record.healthkit_exercise_time = item.exercise_time;
      if (item.stand_hours !== undefined) record.healthkit_stand_hours = item.stand_hours;
      if (item.sleep_time !== undefined) record.healthkit_sleep_time = item.sleep_time;
      if (item.steps !== undefined) record.healthkit_steps = item.steps;
      
      // New fields
      if (item.bmi !== undefined) record.healthkit_bmi = item.bmi;
      if (item.body_mass !== undefined) record.healthkit_body_mass = item.body_mass;
      if (item.heart_rate !== undefined) record.healthkit_heart_rate = item.heart_rate;
      if (item.active_energy !== undefined) record.healthkit_active_energy = item.active_energy;
      
      return record;
    });

    // Upsert in chunks
    for (let i = 0; i < entryRecords.length; i += CHUNK_SIZE) {
      const chunk = entryRecords.slice(i, i + CHUNK_SIZE);
      const { error: entriesError } = await supabase.from('daily_entries').upsert(chunk, {
        onConflict: 'user_id,entry_date',
      });

      if (entriesError) {
        console.error('[HealthKit] Error saving to daily_entries table:', entriesError);
        if (entriesError.message?.includes('column') || entriesError.message?.includes('does not exist')) {
          console.warn('[HealthKit] Some healthkit_* columns may not exist in daily_entries yet. Run the migration.');
          break; // Don't try more chunks if columns are missing
        }
        throw entriesError;
      }
    }

    console.log(`[HealthKit] Successfully saved ${data.length} day(s) of data to both tables`);
  } catch (error) {
    console.error('[HealthKit] Error in saveHealthKitData:', error);
    throw error;
  }
};

/**
 * Get HealthKit data for a specific date range from the database
 */
export const getHealthKitData = async (
  userId: string,
  startDate: string,
  endDate: string
): Promise<HealthKitData[]> => {
  const { data, error } = await supabase
    .from('healthkit_data')
    .select('*')
    .eq('user_id', userId)
    .gte('date', startDate)
    .lte('date', endDate)
    .order('date', { ascending: false });

  if (error) {
    console.error('Error fetching HealthKit data:', error);
    return [];
  }

  // Map the data to ensure proper typing, especially for workouts JSONB field
  return data.map(row => ({
    ...row,
    workouts: row.workouts as any, // JSONB field from database
  })) as HealthKitData[];
};

/**
 * Initialize HealthKit integration
 * Checks availability and requests permissions based on user settings
 */
export const initializeHealthKit = async (userId: string): Promise<boolean> => {
  try {
    // Check if HealthKit is available
    const isAvailable = await checkHealthKitAvailability();
    if (!isAvailable) {
      console.log('HealthKit not available on this device');
      return false;
    }

    // Get user's settings (or use defaults if first time)
    const settings = await getHealthKitSettings(userId);
    
    // Build list of read types based on settings
    // If no settings exist yet (first time), request all permissions
    const readTypes: string[] = [];
    
    if (!settings) {
      // First time setup - request only supported permissions
      // NOTE: Only Steps and Sleep are currently supported
      // Coming soon: Exercise time, stand hours, BMI, body mass, heart rate, active energy
      console.log('HealthKit: First time setup, requesting supported permissions');
      readTypes.push('STEPS', 'SLEEP_ANALYSIS');
    } else {
      // Use user's preferences
      // NOTE: Exercise time and stand hours are disabled (coming soon)
      // if (settings.sync_exercise_time || settings.sync_stand_hours) {
      //   readTypes.push('ACTIVITY_SUMMARY');
      // }
      if (settings.sync_steps) {
        readTypes.push('STEPS');
      }
      if (settings.sync_sleep) {
        readTypes.push('SLEEP_ANALYSIS');
      }
      if (settings.sync_bmi) {
        readTypes.push('BMI');
      }
      if (settings.sync_body_mass) {
        readTypes.push('BODY_MASS');
      }
      if (settings.sync_heart_rate) {
        readTypes.push('HEART_RATE');
      }
      if (settings.sync_active_energy) {
        readTypes.push('ACTIVE_ENERGY');
      }
    }

    if (readTypes.length === 0) {
      console.log('HealthKit: No data types selected for sync. Please enable at least one metric.');
      // Return false to indicate initialization failed due to no metrics selected
      return false;
    }

    // Request authorization - this will show the Apple permission popup
    // Note: res.status may be false even if user sees the popup for the first time
    // or if they've granted partial permissions. We should proceed anyway.
    await requestHealthKitAuthorization(readTypes);
    
    console.log('HealthKit authorization requested - permissions popup shown');
    return true;
  } catch (error) {
    console.error('Error initializing HealthKit:', error);
    return false;
  }
};

/**
 * Sync HealthKit data since last sync (or 365 days if no last sync).
 * Uses batch API calls to fetch the entire date range at once per metric,
 * rather than making individual calls per day.
 */
export const syncTodayHealthKitData = async (userId: string): Promise<boolean> => {
  try {
    // Get user's settings
    const settings = await getHealthKitSettings(userId);
    
    // If no settings exist, don't sync (user hasn't configured HealthKit yet)
    if (!settings) {
      console.log('HealthKit: No settings found - user needs to configure HealthKit first');
      if (typeof window !== 'undefined') {
        (window as any).__healthKitSyncError = 'HealthKit not configured - please enable it in settings';
      }
      return false;
    }
    
    // Check if HealthKit is enabled
    if (!settings.enabled) {
      console.log('HealthKit: Sync skipped - HealthKit is disabled');
      return false;
    }

    // Check if running in native app
    const detection = await detectNativeApp();
    if (!detection.isNative || typeof window === 'undefined' || !window.NativelyHealth) {
      console.log('[HealthKit] Not available (not in native app or SDK not loaded)');
      if (typeof window !== 'undefined') {
        (window as any).__healthKitSyncError = 'HealthKit not available on this device';
      }
      return false;
    }

    const health = new window.NativelyHealth();
    
    // Calculate date range
    const now = new Date();
    const todayStr = format(now, 'yyyy-MM-dd');
    
    const endDate = new Date();
    endDate.setHours(23, 59, 59, 999);
    
    let startDate: Date;
    if ((settings as any).last_sync_at) {
      startDate = new Date((settings as any).last_sync_at);
      startDate.setHours(0, 0, 0, 0);
      console.log('[HealthKit] Syncing since last sync date:', startDate.toISOString());
    } else {
      startDate = new Date();
      startDate.setDate(startDate.getDate() - 365);
      startDate.setHours(0, 0, 0, 0);
      console.log('[HealthKit] No previous sync found, syncing last 365 days from:', startDate.toISOString());
    }
    const startDateStr = format(startDate, 'yyyy-MM-dd');
    
    // Request authorization
    const readDataTypes: string[] = [];
    if (settings.sync_steps) readDataTypes.push('STEPS');
    if (settings.sync_sleep) readDataTypes.push('SLEEP_ANALYSIS');
    
    if (readDataTypes.length > 0) {
      console.log('[HealthKit] Requesting authorization for:', readDataTypes);
      await new Promise<void>((resolve, reject) => {
        const timeout = setTimeout(() => {
          console.error('[HealthKit] Authorization timeout');
          reject(new Error('HealthKit authorization request timeout'));
        }, 15000);
        health.requestAuthorization([], readDataTypes, (res: any, err: any) => {
          clearTimeout(timeout);
          if (err) {
            console.error('[HealthKit] Authorization error:', err);
            reject(new Error(String(err)));
            return;
          }
          console.log('[HealthKit] Authorization response:', res?.status);
          resolve();
        });
      });
    }
    
    // Collect data across all days: date string -> HealthKitData
    const dataByDate = new Map<string, HealthKitData>();
    const syncResults: Array<{ metric: string; success: boolean; error?: string; value?: any; daysWithData?: number }> = [];
    
    // ========================================
    // BATCH FETCH STEPS (one call for full range)
    // ========================================
    if (settings.sync_steps) {
      console.log(`[HealthKit] Fetching STEPS for range: ${startDateStr} to ${todayStr}`);
      try {
        const stepsData = await new Promise<any>((resolve, reject) => {
          const timeout = setTimeout(() => reject(new Error('Steps fetch timeout (30s)')), 30000);
          health.getStatisticQuantity('STEPS', 'DAY', startDate, endDate, (res: any, err: any) => {
            clearTimeout(timeout);
            if (err) {
              reject(new Error(typeof err === 'string' ? err : JSON.stringify(err)));
              return;
            }
            resolve(res);
          });
        });
        
        console.log('[HealthKit] Steps response received, result count:', stepsData?.result?.length ?? 'N/A');
        
        if (stepsData?.result && Array.isArray(stepsData.result)) {
          let daysWithData = 0;
          let todayValue: number | null = null;
          
          for (const item of stepsData.result) {
            // Extract the value
            let stepsValue: number | null = null;
            if (typeof item === 'object' && item.value !== undefined && item.value !== null) {
              stepsValue = Number(item.value);
            } else if (typeof item === 'number') {
              stepsValue = item;
            }
            
            if (stepsValue == null || isNaN(stepsValue) || stepsValue <= 0) continue;
            
            // Determine the date for this data point
            let dateStr: string;
            if (item.startDate) {
              dateStr = format(normalizeHealthKitTimestamp(item.startDate), 'yyyy-MM-dd');
            } else if (item.date) {
              dateStr = typeof item.date === 'string' 
                ? item.date.split('T')[0] 
                : format(normalizeHealthKitTimestamp(item.date), 'yyyy-MM-dd');
            } else {
              continue; // Can't determine date, skip
            }
            
            if (!dataByDate.has(dateStr)) dataByDate.set(dateStr, { date: dateStr });
            const steps = Math.round(stepsValue);
            dataByDate.get(dateStr)!.steps = steps;
            daysWithData++;
            
            if (dateStr === todayStr) todayValue = steps;
          }
          
          console.log(`[HealthKit] Steps: ${daysWithData} days with data`);
          syncResults.push({
            metric: 'Steps',
            success: daysWithData > 0,
            value: todayValue,
            daysWithData,
          });
        } else {
          syncResults.push({ metric: 'Steps', success: false, error: 'No data returned from HealthKit' });
        }
      } catch (err) {
        console.error('[HealthKit] Steps batch fetch error:', err);
        syncResults.push({ metric: 'Steps', success: false, error: err instanceof Error ? err.message : String(err) });
      }
    }
    
    // ========================================
    // BATCH FETCH SLEEP (one call for full range)
    // ========================================
    if (settings.sync_sleep) {
      console.log(`[HealthKit] Fetching SLEEP_ANALYSIS for range: ${startDateStr} to ${todayStr}`);
      try {
        const sleepData = await new Promise<any>((resolve, reject) => {
          const timeout = setTimeout(() => reject(new Error('Sleep fetch timeout (30s)')), 30000);
          health.getDailySleepAnalysis(startDate, endDate, 0, (res: any, err: any) => {
            clearTimeout(timeout);
            if (err) {
              reject(new Error(typeof err === 'string' ? err : JSON.stringify(err)));
              return;
            }
            resolve(res);
          });
        });
        
        console.log('[HealthKit] Sleep response received, result count:', sleepData?.result?.length ?? 'N/A');
        
        if (sleepData?.result && Array.isArray(sleepData.result) && sleepData.result.length > 0) {
          const sleepByDate = groupSleepEntriesByDate(sleepData.result);
          let daysWithData = 0;
          let todayValue: number | null = null;
          
          for (const [dateStr, hours] of sleepByDate) {
            if (hours > 0) {
              if (!dataByDate.has(dateStr)) dataByDate.set(dateStr, { date: dateStr });
              dataByDate.get(dateStr)!.sleep_time = parseFloat(hours.toFixed(2));
              daysWithData++;
              
              if (dateStr === todayStr) todayValue = hours;
            }
          }
          
          console.log(`[HealthKit] Sleep: ${daysWithData} days with data`);
          syncResults.push({
            metric: 'Sleep Time',
            success: daysWithData > 0,
            value: todayValue,
            daysWithData,
          });
        } else {
          syncResults.push({ metric: 'Sleep Time', success: false, error: 'No sleep data returned from HealthKit' });
        }
      } catch (err) {
        console.error('[HealthKit] Sleep batch fetch error:', err);
        syncResults.push({ metric: 'Sleep Time', success: false, error: err instanceof Error ? err.message : String(err) });
      }
    }
    
    // ========================================
    // SAVE ALL DATA
    // ========================================
    const allData = Array.from(dataByDate.values());
    console.log(`[HealthKit] Batch sync collected ${allData.length} days of data`);
    
    if (allData.length > 0) {
      console.log('[HealthKit] Saving batch data to database...');
      await saveHealthKitData(userId, allData);
      
      console.log('[HealthKit] Logging detailed entries...');
      await logHealthKitDetailedEntries(userId, allData, startDateStr, todayStr);
    }
    
    // Update last sync time
    await supabase
      .from('healthkit_settings')
      .update({ last_sync_at: new Date().toISOString() })
      .eq('user_id', userId);
    
    // Store results for UI
    console.log('[HealthKit] ===== BATCH SYNC RESULTS =====');
    syncResults.forEach(result => {
      if (result.success) {
        console.log(`[HealthKit] ✅ ${result.metric}: ${result.daysWithData} days synced${result.value != null ? ` (today: ${result.value})` : ''}`);
      } else {
        console.log(`[HealthKit] ❌ ${result.metric}: ${result.error}`);
      }
    });
    console.log('[HealthKit] ===============================');
    
    if (typeof window !== 'undefined') {
      (window as any).__healthKitLastSyncResults = syncResults;
      delete (window as any).__healthKitSyncError;
    }
    
    return true;
  } catch (error) {
    console.error('[HealthKit] Error in syncTodayHealthKitData:', error);
    console.error('[HealthKit] Error details:', error instanceof Error ? error.message : String(error));
    
    // Store error details for UI to display
    if (typeof window !== 'undefined') {
      (window as any).__healthKitSyncError = error instanceof Error ? error.message : String(error);
    }
    
    return false;
  }
};
